import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { PublisherViewStore } from "../../store/PublisherViewStore";
import { useRootStore } from "../../../../store/contexts";
import { PersonType } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { MaritalStatus } from "../../../../api/graphql";
import { ShowReference } from "../../../common/controls";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { FormControl } from "@mui/material";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { ShowDateTime } from "../../../common/controls";

export type PublisherViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  personTypeOptions: { id: string; displayId: string }[];
  personType: PersonType;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  maritalStatusOptions: { id: string; displayId: string }[];
  maritalStatus: MaritalStatus | "";
  nationalityValue: { displayId: string; id: string } | null;
  pseudonymValue?: { displayId: string; id: string } | null;
  pseudonym: string;
  taxIdValue?: { displayId: string; id: string } | null;
  taxId: string;
  phoneValue?: { displayId: string; id: string } | null;
  phone: string;
  emailValue?: { displayId: string; id: string } | null;
  email: string;
  streetAndNumberValue?: { displayId: string; id: string } | null;
  streetAndNumber: string;
  floorValue?: { displayId: string; id: string } | null;
  floor: string;
  suiteValue?: { displayId: string; id: string } | null;
  suite: string;
  postalCodeValue?: { displayId: string; id: string } | null;
  postalCode: string;
  countryValue: { displayId: string; id: string } | null;
  provinceValue: { displayId: string; id: string } | null;
  cityValue?: { displayId: string; id: string } | null;
  city: string;
  caeIpiValue?: { displayId: string; id: string } | null;
  caeIpi: string;
  representativesRows: CrudRows;
  representativesVisibleColumns: GridColumn[];
  representativesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  managementSocietiesRows: CrudRows;
  managementSocietiesVisibleColumns: GridColumn[];
  managementSocietiesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  createdAt_Value?: { displayId: string; id: string } | null;
  createdAt_: Date | null;
  lastUpdatedAt_Value?: { displayId: string; id: string } | null;
  lastUpdatedAt_: Date | null;
  publisherViewStore: PublisherViewStore;
};

export const PublisherViewForm: React.FC<PublisherViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    personTypeOptions = [],
    personType = "" as PersonType,
    name = "",
    maritalStatusOptions = [],
    maritalStatus = "" as MaritalStatus | "",
    nationalityValue,
    pseudonym = "",
    taxId = "",
    phone = "",
    email = "",
    streetAndNumber = "",
    floor = "",
    suite = "",
    postalCode = "",
    countryValue,
    provinceValue,
    city = "",
    caeIpi = "",
    representativesRows,
    representativesVisibleColumns,
    representativesSetColumnWiths,
    managementSocietiesRows,
    managementSocietiesVisibleColumns,
    managementSocietiesSetColumnWiths,
    createdAt_ = null,
    lastUpdatedAt_ = null,
    publisherViewStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="basicInformation"
            title={langStore.groupPublisherBasicInformationLabel}
            collapsible={true}
            expanded={publisherViewStore.getCardState("basicInformation")}
            toggleExpand={() =>
              publisherViewStore.toggleCardState("basicInformation")
            }
          >
            <FormControl fullWidth>
              <ShowString
                value={
                  personType
                    ? personTypeOptions.find(
                        (o: { id: string }) => o.id === personType
                      )?.displayId || personType
                    : ""
                }
                label={langStore.PublisherPersonTypeLabel}
              />
              <ShowMultiLine
                value={name}
                label={langStore.PublisherNameLabel}
              />
              <ShowString
                value={
                  maritalStatus
                    ? maritalStatusOptions.find(
                        (o: { id: string }) => o.id === maritalStatus
                      )?.displayId || maritalStatus
                    : ""
                }
                label={langStore.PublisherMaritalStatusLabel}
              />
              <ShowReference
                label={langStore.PublisherNationalityLabel}
                selectedValue={langStore.displayIdByLang(
                  nationalityValue?.displayId
                )}
              />
              <ShowMultiLine
                value={pseudonym}
                label={langStore.PublisherPseudonymLabel}
              />
              <ShowMultiLine
                value={taxId}
                label={langStore.PublisherTaxIdLabel}
              />
              <ShowMultiLine
                value={phone}
                label={langStore.PublisherPhoneLabel}
              />
              <Box
                sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
              >
                <ShowString
                  value={email}
                  label={langStore.PublisherEmailLabel}
                />
                <EmailVerificationIndicator
                  email={email}
                  entityName="Publisher"
                />
              </Box>
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="address"
            title={langStore.groupPublisherAddressLabel}
            collapsible={true}
            expanded={publisherViewStore.getCardState("address")}
            toggleExpand={() => publisherViewStore.toggleCardState("address")}
          >
            <FormControl fullWidth>
              <ShowMultiLine
                value={streetAndNumber}
                label={langStore.PublisherStreetAndNumberLabel}
              />
              <ShowMultiLine
                value={floor}
                label={langStore.PublisherFloorLabel}
              />
              <ShowMultiLine
                value={suite}
                label={langStore.PublisherSuiteLabel}
              />
              <ShowMultiLine
                value={postalCode}
                label={langStore.PublisherPostalCodeLabel}
              />
              <ShowReference
                label={langStore.PublisherCountryLabel}
                selectedValue={langStore.displayIdByLang(
                  countryValue?.displayId
                )}
              />
              <ShowReference
                label={langStore.PublisherProvinceLabel}
                selectedValue={langStore.displayIdByLang(
                  provinceValue?.displayId
                )}
              />
              <ShowMultiLine
                value={city}
                label={langStore.PublisherCityLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="legalInformation"
            title={langStore.groupPublisherLegalInformationLabel}
            collapsible={true}
            expanded={publisherViewStore.getCardState("legalInformation")}
            toggleExpand={() =>
              publisherViewStore.toggleCardState("legalInformation")
            }
          >
            <FormControl fullWidth>
              <ShowMultiLine
                value={caeIpi}
                label={langStore.PublisherCaeIpiLabel}
              />
              <ShowItemsCompact
                rows={representativesRows}
                showLabel={true}
                label={langStore.PublisherRepresentativesLabel}
                columns={representativesVisibleColumns}
                setColumnWidths={representativesSetColumnWiths}
                entityName="PublisherRepresentative"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("publisherRepresentative", rowId)
                }
              />
              <ShowItemsCompact
                rows={managementSocietiesRows}
                showLabel={true}
                label={langStore.PublisherManagementSocietiesLabel}
                columns={managementSocietiesVisibleColumns}
                setColumnWidths={managementSocietiesSetColumnWiths}
                entityName="PublisherCollectingSociety"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal(
                    "publisherCollectingSociety",
                    rowId
                  )
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="systemDates"
            title={langStore.groupPublisherSystemDatesLabel}
            collapsible={true}
            expanded={publisherViewStore.getCardState("systemDates")}
            toggleExpand={() =>
              publisherViewStore.toggleCardState("systemDates")
            }
          >
            <FormControl fullWidth>
              <ShowDateTime
                value={createdAt_}
                label={langStore.PublisherCreatedAt_Label}
              />
              <ShowDateTime
                value={lastUpdatedAt_}
                label={langStore.PublisherLastUpdatedAt_Label}
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
